"use client";

import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Container } from "@/components/ui/container";
import {
  apps,
  legalDocuments,
  isGroupedDocuments,
  type AppKey,
  type LegalDocument,
  type LegalDocumentsGroup,
} from "@/content/data/legal";
import { cn } from "@/lib/utils";

function TabPills({
  selectedApp,
  onSelect,
}: {
  selectedApp: AppKey;
  onSelect: (app: AppKey) => void;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {apps.map((app) => {
        const isSelected = selectedApp === app.key;
        const colorClasses = {
          primary: isSelected
            ? "bg-primary-500 text-white border-primary-500"
            : "bg-panel text-muted border-line hover:border-brand-green-line hover:text-brand-green",
          secondary: isSelected
            ? "bg-ai-action text-ai-on-action border-line-strong"
            : "bg-panel text-muted border-line hover:border-brand-green-line hover:text-brand-green",
          tertiary: isSelected
            ? "bg-sky-500 text-white border-sky-500"
            : "bg-panel text-muted border-line hover:border-brand-blue-line hover:text-brand-blue",
        };

        return (
          <button
            key={app.key}
            onClick={() => onSelect(app.key)}
            className={`px-5 py-2.5 rounded-full border-2 font-medium text-sm transition-all duration-200 ${colorClasses[app.color]}`}
          >
            {app.label}
          </button>
        );
      })}
    </div>
  );
}

function DocumentCard({
  document,
  color,
}: {
  document: LegalDocument;
  color: "primary" | "secondary" | "tertiary";
}) {
  const Icon = document.icon;

  const colorClasses = {
    primary: {
      border: "border-l-4 border-l-primary-500",
      icon: "text-brand-green",
      iconBg: "bg-brand-green-soft",
      arrow: "text-brand-green group-hover:text-brand-green",
      hoverBorder: "group-hover:border-brand-green-line",
    },
    secondary: {
      border: "border-l-4 border-l-primary-500",
      icon: "text-brand-green",
      iconBg: "bg-brand-green-soft",
      arrow: "text-brand-green group-hover:text-brand-green",
      hoverBorder: "group-hover:border-brand-green-line",
    },
    tertiary: {
      border: "border-l-4 border-l-sky-500",
      icon: "text-brand-blue",
      iconBg: "bg-brand-blue-soft",
      arrow: "text-brand-blue group-hover:text-brand-blue",
      hoverBorder: "group-hover:border-brand-blue-line",
    },
  };

  const classes = colorClasses[color];

  return (
    <Link href={document.href} className="group block">
      <div
        className={cn(
          "flex items-center justify-between p-4 rounded-xl bg-panel border border-line shadow-sm transition-all duration-200",
          "hover:shadow-lg hover:-translate-y-0.5",
          classes.border,
          classes.hoverBorder
        )}
      >
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
              classes.iconBg
            )}
          >
            <Icon className={cn("w-6 h-6", classes.icon)} />
          </div>
          <span className="font-semibold text-ink">{document.title}</span>
        </div>
        <ArrowRight
          className={cn(
            "w-5 h-5 transition-transform duration-200 group-hover:translate-x-1",
            classes.arrow
          )}
        />
      </div>
    </Link>
  );
}

function DocumentsGrid({
  documents,
  color,
}: {
  documents: LegalDocument[];
  color: "primary" | "secondary" | "tertiary";
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {documents.map((doc) => (
        <DocumentCard key={doc.href} document={doc} color={color} />
      ))}
    </div>
  );
}

function GroupedDocuments({
  groups,
  color,
}: {
  groups: LegalDocumentsGroup[];
  color: "primary" | "secondary" | "tertiary";
}) {
  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <div key={group.label}>
          <h3 className="text-sm font-semibold text-muted uppercase tracking-wider mb-4">
            {group.label}
          </h3>
          <DocumentsGrid documents={group.documents} color={color} />
        </div>
      ))}
    </div>
  );
}

export function LegalHubSection({ query = "" }: { query?: string }) {
  const searchParams = new URLSearchParams(query);
  const router = useRouter();

  const appParam = searchParams.get("app") as AppKey | null;
  const selectedApp: AppKey =
    appParam && apps.some((a) => a.key === appParam) ? appParam : "buddify";

  const handleSelect = (app: AppKey) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("app", app);
    router.push(`/legal?${params.toString()}`, { scroll: false });
  };

  const currentDocs = legalDocuments[selectedApp];
  const currentApp = apps.find((a) => a.key === selectedApp)!;

  return (
    <>
      {/* Hero header */}
      <section
        className={cn(
          "relative overflow-hidden pt-24 pb-12 md:pt-32 md:pb-16",
          currentApp.color === "tertiary"
            ? "bg-gradient-to-b from-brand-blue-soft to-panel"
            : "bg-gradient-to-b from-brand-green-soft to-panel"
        )}
      >
        <Container size="md">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div
              className={cn(
                "inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6",
                currentApp.color === "tertiary"
                  ? "bg-brand-blue-soft text-brand-blue"
                  : "bg-brand-green-soft text-brand-green"
              )}
            >
              <Shield className="w-7 h-7" />
            </div>

            <h1 className="text-h1 text-ink mb-4">Legal Center</h1>
            <p className="text-body-lg text-muted max-w-3xl mx-auto mb-3">
              Access all our policies, terms, and legal documents for each of our
              products.
            </p>
            <p className="text-sm text-muted">
              Documents are reviewed regularly and kept up to date.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Documents section */}
      <section className="py-10 md:py-14 bg-page">
        <Container size="md">
          {/* Tab Pills */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-10"
          >
            <TabPills selectedApp={selectedApp} onSelect={handleSelect} />
          </motion.div>

          {/* Documents */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={selectedApp}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="rounded-2xl border border-line bg-panel p-6 md:p-8"
            >
              {isGroupedDocuments(currentDocs) ? (
                <GroupedDocuments groups={currentDocs} color={currentApp.color} />
              ) : (
                <DocumentsGrid documents={currentDocs} color={currentApp.color} />
              )}
            </motion.div>
          </AnimatePresence>
        </Container>
      </section>
    </>
  );
}
